import React, { useState, useEffect, useRef } from "react";
import { X, Upload, FileText, Check, ChevronDown, Loader2 } from "lucide-react";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";

const OCRtoPO = ({ isOpen, onClose, organisationId }) => {
    const [vendors, setVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [showVendorDropdown, setShowVendorDropdown] = useState(false);
    const [result, setResult] = useState(null);
    const { addAlert } = useAlert();
    const vendorRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (isOpen && organisationId) {
            fetchVendors();
        }
    }, [isOpen, organisationId]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (vendorRef.current && !vendorRef.current.contains(event.target)) {
                setShowVendorDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const fetchVendors = async () => {
        try {
            const token = localStorage.getItem("access");
            const response = await fetch(
                `${BASE_URL}/inventory/list-org-vendors/${organisationId}/`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const result = await response.json();

            if (response.ok && result.status && Array.isArray(result.data)) {
                setVendors(result.data);
            } else {
                console.error("Failed to load vendors", result.message);
            }
        } catch (error) {
            console.error("Error fetching vendors:", error);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            setFile(droppedFile);
        }
    };

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
        }
    };

    const handleSubmit = async () => {
        if (!file) {
            addAlert("Please upload a bill document", "error");
            return;
        }

        setIsLoading(true);
        const formData = new FormData();
        formData.append("organization_id", organisationId);
        formData.append("bill", file);
        if (selectedVendor) {
            formData.append("vendor_id", selectedVendor.id);
        }

        try {
            const token = localStorage.getItem("access");
            const response = await fetch(
                `${BASE_URL}/inventory/purchase-orders/create-from-document/`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                }
            );

            const data = await response.json();

            if (response.ok && data.status) {
                setResult(data);
                addAlert("Document successfully parsed", "success");
            } else {
                addAlert(data.message || "Failed to parse document", "error");
            }
        } catch (error) {
            console.error("Error submitting bill:", error);
            addAlert("An error occurred while processing the document", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setSelectedVendor(null);
        setResult(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <h2 className="text-xl font-semibold text-gray-800">
                        Create PO from Bill
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {!result ? (
                        <div className="space-y-6">
                            {/* Vendor Selection */}
                            <div className="space-y-2" ref={vendorRef}>
                                <label className="block text-sm font-medium text-gray-700">
                                    Select Vendor (Optional)
                                </label>
                                <div className="relative">
                                    <div
                                        onClick={() => setShowVendorDropdown(!showVendorDropdown)}
                                        className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg cursor-pointer flex items-center justify-between hover:border-[#3B0069] transition-colors"
                                    >
                                        <span
                                            className={
                                                selectedVendor ? "text-gray-900" : "text-gray-500"
                                            }
                                        >
                                            {selectedVendor ? selectedVendor.name : "Choose a vendor"}
                                        </span>
                                        <ChevronDown size={18} className="text-gray-400" />
                                    </div>

                                    {showVendorDropdown && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                            {vendors.length === 0 ? (
                                                <div className="p-3 text-sm text-gray-500 text-center">
                                                    No vendors available
                                                </div>
                                            ) : (
                                                vendors.map((vendor) => (
                                                    <div
                                                        key={vendor.id}
                                                        onClick={() => {
                                                            setSelectedVendor(vendor);
                                                            setShowVendorDropdown(false);
                                                        }}
                                                        className="px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors"
                                                    >
                                                        {vendor.name}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* File Upload */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Upload Bill
                                </label>
                                <div
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${isDragging
                                            ? "border-[#3B0069] bg-[#F5EEF9]"
                                            : "border-gray-300 hover:border-[#3B0069] hover:bg-gray-50"
                                        }`}
                                >
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileSelect}
                                        className="hidden"
                                        accept=".pdf,.png,.jpg,.jpeg"
                                    />
                                    {file ? (
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                                                <Check size={24} />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {file.name}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                                </p>
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setFile(null);
                                                }}
                                                className="text-xs text-red-500 hover:text-red-700 font-medium"
                                            >
                                                Remove file
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="w-12 h-12 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center">
                                                <Upload size={24} />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-700">
                                                    Click to upload or drag and drop
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    PDF, PNG, JPG up to 10MB
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Submit Button */}
                            <button
                                onClick={handleSubmit}
                                disabled={isLoading || !file}
                                className={`w-full py-3 px-4 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-all ${isLoading || !file
                                        ? "bg-gray-300 cursor-not-allowed"
                                        : "bg-[#3B0069] hover:bg-[#2d0050] shadow-md hover:shadow-lg"
                                    }`}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 size={20} className="animate-spin" />
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <FileText size={20} />
                                        Process Document
                                    </>
                                )}
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {/* Vendor Details */}
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                    Vendor Details
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-gray-500">Name</p>
                                        <p className="text-sm font-medium text-gray-900">
                                            {result.vendor?.vendor_name || "N/A"}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Contact</p>
                                        <p className="text-sm font-medium text-gray-900">
                                            {result.vendor?.vendor_contact || "N/A"}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Type</p>
                                        <p className="text-sm font-medium text-gray-900">
                                            {result.vendor?.vendor_type || "N/A"}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Delivery Date</p>
                                        <p className="text-sm font-medium text-gray-900">
                                            {result.delivery_date || "N/A"}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Matched Materials */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                    Matched Materials
                                </h3>
                                {result.matched_materials &&
                                    result.matched_materials.length > 0 ? (
                                    <div className="overflow-hidden border border-gray-200 rounded-lg">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Material
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Spec
                                                    </th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Qty
                                                    </th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Rate
                                                    </th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Total
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {result.matched_materials.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                            {item.material_name}
                                                            <div className="text-xs text-gray-500">
                                                                {item.brand_name}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-500">
                                                            {item.specification || "-"}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                            {item.quantity} {item.uom}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                            ₹{item.unit_price}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                                                            ₹{item.total_amount}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500 italic">
                                        No matched materials found.
                                    </p>
                                )}
                            </div>

                            {/* Unmatched Materials */}
                            {result.unmatched_materials &&
                                result.unmatched_materials.length > 0 && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                                            Unmatched Materials
                                        </h3>
                                        <div className="space-y-3">
                                            {result.unmatched_materials.map((item, idx) => (
                                                <div
                                                    key={idx}
                                                    className="flex items-center justify-between p-4 bg-orange-50 border border-orange-100 rounded-lg"
                                                >
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {item.material_name}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {item.subcategory} • {item.uom}
                                                        </p>
                                                    </div>
                                                    <button className="px-3 py-1.5 text-xs font-medium text-[#3B0069] bg-white border border-[#3B0069] rounded-md hover:bg-[#F5EEF9] transition-colors">
                                                        Add to Library
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                            {/* Actions */}
                            <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
                                <button
                                    onClick={() => setResult(null)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Back
                                </button>
                                <button className="px-4 py-2 text-sm font-medium text-white bg-[#3B0069] rounded-lg hover:bg-[#2d0050] shadow-sm">
                                    Create PO
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OCRtoPO;
